import {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import {chatErrorMessage, userDisplayName} from './chatUtils';

const TYPES = [
    {value: 'CHANNEL', label: 'Channel'},
    {value: 'DIRECT', label: 'Direct message'},
    {value: 'GROUP_DM', label: 'Group message'},
];

export default function ConversationModal({show, onHide, onCreated, chat}) {
    const [kind, setKind] = useState('CHANNEL');
    const [name, setName] = useState('');
    const [selected, setSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const {refreshDirectory} = chat;
    const peers = useMemo(() => chat.users.filter(user => user.id !== chat.currentUser?.id), [chat.users, chat.currentUser?.id]);
    const adminUsers = chat.adminUsers || chat.users;

    useEffect(() => {
        if (!show) return;
        setKind('CHANNEL');
        setName('');
        setSelected([]);
        setError(null);
        refreshDirectory().catch(setError);
    }, [show, refreshDirectory]);

    function toggleUser(userId) {
        if (kind === 'DIRECT') {
            setSelected([userId]);
            return;
        }
        setSelected(current => current.includes(userId)
            ? current.filter(id => id !== userId)
            : [...current, userId]);
    }

    async function submit(event) {
        event.preventDefault();
        setError(null);
        if (!chat.currentUser) return;
        if (kind === 'CHANNEL' && !name.trim()) {
            setError(new Error('Channel name is required.'));
            return;
        }
        if (kind === 'DIRECT' && selected.length !== 1) {
            setError(new Error('Choose one person for a direct message.'));
            return;
        }
        if (kind === 'GROUP_DM' && selected.length < 2) {
            setError(new Error('Choose at least two other people for a group message.'));
            return;
        }
        setSubmitting(true);
        try {
            let memberUserIds;
            if (kind === 'CHANNEL') {
                const directory = await chat.refreshDirectory();
                memberUserIds = (directory.admin_users || adminUsers).map(user => user.id);
            } else {
                memberUserIds = [chat.currentUser.id, ...selected];
            }
            const response = await chat.createConversation({
                kind,
                name: kind === 'CHANNEL' ? name.trim() : undefined,
                member_user_ids: memberUserIds,
                access_policy_code: 'ADMIN_MANAGED',
            });
            onCreated(response.conversation, response.reused);
            onHide();
        } catch (submitError) {
            setError(submitError);
            if (submitError.code === 'INACTIVE_MEMBER') chat.refreshDirectory().catch(() => {});
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title>New conversation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error ? <Alert variant="danger">{chatErrorMessage(error)}</Alert> : null}
                    <div className="chat-kind-picker" role="group" aria-label="Conversation type">
                        {TYPES.map(type => (
                            <Button
                                key={type.value}
                                type="button"
                                variant={kind === type.value ? 'primary' : 'outline-secondary'}
                                onClick={() => {
                                    setKind(type.value);
                                    setSelected([]);
                                    setError(null);
                                }}
                            >
                                {type.label}
                            </Button>
                        ))}
                    </div>
                    {kind === 'CHANNEL' ? (
                        <>
                            <Form.Group className="mt-4" controlId="chat-channel-name">
                                <Form.Label>Channel name</Form.Label>
                                <Form.Control
                                    autoFocus
                                    value={name}
                                    maxLength={120}
                                    placeholder="e.g. operations"
                                    onChange={event => setName(event.target.value)}
                                />
                            </Form.Group>
                            <p className="text-muted mt-3 mb-0">
                                All {adminUsers.length} active Admins will be able to read and post in this channel.
                            </p>
                        </>
                    ) : (
                        <div className="chat-admin-picker mt-4" aria-label="Chat members">
                            <div className="chat-admin-picker-title">
                                {kind === 'DIRECT' ? 'Choose a person' : 'Choose at least two people'}
                            </div>
                            {peers.length === 0 ? <p className="text-muted mt-3">No other active Chat users are available.</p> : null}
                            {peers.map(user => (
                                <Form.Check
                                    key={user.id}
                                    type={kind === 'DIRECT' ? 'radio' : 'checkbox'}
                                    name={kind === 'DIRECT' ? 'direct-admin' : undefined}
                                    id={`chat-user-${user.id}`}
                                    checked={selected.includes(user.id)}
                                    onChange={() => toggleUser(user.id)}
                                    label={<span><strong>{userDisplayName(user)}</strong> <span className="text-muted">@{user.username} · {user.role || 'ADMIN'}</span></span>}
                                />
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="outline-secondary" onClick={onHide}>Cancel</Button>
                    <Button type="submit" disabled={submitting || !chat.currentUser}>
                        {submitting ? <><Spinner size="sm" className="me-2" />Creating…</> : 'Create'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
